import { useCallback, useEffect, useState, type FormEvent } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api } from "../api.js";
import { useSelectedTenant } from "../shell/tenant.js";
import { FieldReason, ifMatch, RefusalNotice, toRefusal, type Refusal } from "../zd/refusal.js";

// S-050 prices section (spec BILL-01, BILL-09, 3.3 "change price group"; screens Z03 and Z04,
// whose full screens are S-046 and S-051). Lists the selected tenant's prices per module with the
// one in effect today, and adds a price with an effective date. The server decides everything;
// refusals follow the ZD screen error pattern (refusal.tsx, BD-63) and keep what was entered.

type Module = "lite" | "remembrance";
type Group = "A" | "B" | "C";
const GROUPS: readonly Group[] = ["A", "B", "C"];

export interface PriceView {
  id: string;
  module: Module;
  amountExclVatCents: number;
  amountInclVatCents: number | null;
  priceGroup: Group | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  reason: string | null;
  inEffectToday: boolean;
}

interface ModulePrices {
  version: number;
  inEffect: PriceView | null;
  prices: PriceView[];
}

export interface PricesAnswer {
  tenant: { id: string; name: string; status: string };
  today: string;
  onboardingPriceGroup: Group | null;
  vatRateBasisPoints: number | null;
  defaults: { liteMonthly: number | null; remembrance: Record<Group, number | null> };
  modules: Record<Module, ModulePrices>;
}

/** "79", "79.5", "79,50" or "1 000.00" rand as cents; undefined when not an amount (two decimals at most). */
export function randToCents(text: string): number | undefined {
  const s = text.replace(/[\s ]/g, "").replace(",", ".");
  const m = /^(\d{1,6})(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return undefined;
  return Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
}

export const formatRand = (cents: number) =>
  `R${new Intl.NumberFormat("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)}`;

function status(p: PriceView, today: string) {
  if (p.inEffectToday) return t("console.prices.status_in_effect");
  return p.effectiveFrom > today ? t("console.prices.status_scheduled") : t("console.prices.status_ended");
}

function PriceTable({ m, data }: { m: Module; data: PricesAnswer }) {
  const mod = data.modules[m];
  return (
    <section aria-labelledby={`prices-${m}`}>
      <h3 id={`prices-${m}`}>{m === "lite" ? t("console.prices.section_lite") : t("console.prices.section_remembrance")}</h3>
      <p data-testid={`in-effect-${m}`}>
        {mod.inEffect ? (
          <>
            {t("console.prices.in_effect_today")} {mod.inEffect.priceGroup && <strong>{mod.inEffect.priceGroup} </strong>}
            {formatRand(mod.inEffect.amountExclVatCents)}
          </>
        ) : (
          t("console.prices.not_in_effect")
        )}
      </p>
      {mod.prices.length === 0 ? (
        <p>{t("console.prices.none_set")}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th scope="col">{t("console.prices.col_from")}</th>
              <th scope="col">{t("console.prices.col_to")}</th>
              {m === "remembrance" && <th scope="col">{t("console.prices.col_group")}</th>}
              <th scope="col">{t("console.prices.col_excl_vat")}</th>
              <th scope="col">{t("console.prices.col_incl_vat")}</th>
              <th scope="col">{t("console.prices.col_status")}</th>
              <th scope="col">{t("console.prices.col_reason")}</th>
            </tr>
          </thead>
          <tbody>
            {mod.prices.map((p) => (
              <tr key={p.id}>
                <td>{p.effectiveFrom}</td>
                <td>{p.effectiveTo ?? t("console.prices.open_ended")}</td>
                {m === "remembrance" && <td>{p.priceGroup}</td>}
                <td>{formatRand(p.amountExclVatCents)}</td>
                <td>{p.amountInclVatCents === null ? t("console.prices.vat_not_set") : formatRand(p.amountInclVatCents)}</td>
                <td>{status(p, data.today)}</td>
                <td>{p.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface Form {
  module: Module;
  priceGroup: Group | "";
  amount: string;
  effectiveFrom: string;
  reason: string;
}

function AddPrice({ data, onAdded, onReload }: { data: PricesAnswer; onAdded: () => void; onReload: () => void }) {
  const [form, setForm] = useState<Form>(() => ({
    module: "remembrance",
    priceGroup: data.onboardingPriceGroup ?? "",
    amount: "",
    effectiveFrom: data.today,
    reason: "",
  }));
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const fallback =
    form.module === "lite" ? data.defaults.liteMonthly : form.priceGroup ? data.defaults.remembrance[form.priceGroup] : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setSaved(false);
    const cents = randToCents(form.amount);
    const r = await api<{ version: number }>("/v1/zd/prices", {
      method: "POST",
      headers: ifMatch(data.modules[form.module].version),
      body: {
        tenantId: data.tenant.id,
        module: form.module,
        priceGroup: form.module === "remembrance" ? form.priceGroup || null : null,
        // An entry that is not an amount is sent as such, so the server gives the field's reason.
        amountExclVatCents: cents ?? form.amount,
        effectiveFrom: form.effectiveFrom,
        reason: form.reason || null,
      },
    });
    setBusy(false);
    const refused = toRefusal(r);
    setRefusal(refused);
    if (!refused) {
      setSaved(true);
      setForm((f) => ({ ...f, amount: "", reason: "" }));
      onAdded();
    }
  }

  return (
    <form onSubmit={submit} noValidate aria-label={t("console.prices.add_title")}>
      <h3>{t("console.prices.add_title")}</h3>
      <RefusalNotice refusal={refusal} onReload={onReload} />
      {saved && <p role="status">{t("console.prices.saved")}</p>}
      <div className="field">
        <label>
          {t("console.prices.field_module")}
          <select name="module" value={form.module} onChange={(e) => set("module", e.target.value as Module)}>
            <option value="remembrance">{t("console.prices.module_remembrance")}</option>
            <option value="lite">{t("console.prices.module_lite")}</option>
          </select>
        </label>
        <FieldReason refusal={refusal} field="module" />
      </div>
      {form.module === "remembrance" && (
        <div className="field">
          <label>
            {t("console.prices.field_group")}
            <select name="priceGroup" value={form.priceGroup} onChange={(e) => set("priceGroup", e.target.value as Group | "")}>
              <option value="">{t("console.prices.group_choose")}</option>
              {GROUPS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </label>
          <FieldReason refusal={refusal} field="priceGroup" />
        </div>
      )}
      <div className="field">
        <label>
          {t("console.prices.field_amount")}
          <input name="amount" inputMode="decimal" maxLength={12} value={form.amount} onChange={(e) => set("amount", e.target.value)} />
        </label>
        <p className="hint">
          {fallback === null ? (
            t("console.prices.default_not_set")
          ) : (
            <>
              {t("console.prices.default_amount")}{" "}
              <button type="button" onClick={() => set("amount", (fallback / 100).toFixed(2))}>
                {formatRand(fallback)}
              </button>
            </>
          )}
        </p>
        <FieldReason refusal={refusal} field="amountExclVatCents" />
      </div>
      <div className="field">
        <label>
          {t("console.prices.field_from")}
          <input name="effectiveFrom" type="date" min={data.today} value={form.effectiveFrom} onChange={(e) => set("effectiveFrom", e.target.value)} />
        </label>
        <p className="hint">{t("console.prices.field_from_hint")}</p>
        <FieldReason refusal={refusal} field="effectiveFrom" />
      </div>
      <div className="field">
        <label>
          {t("console.prices.field_reason")}
          <input name="reason" maxLength={500} value={form.reason} onChange={(e) => set("reason", e.target.value)} />
        </label>
        <FieldReason refusal={refusal} field="reason" />
      </div>
      <button type="submit" disabled={busy}>
        {busy ? t("console.prices.saving") : t("console.prices.submit")}
      </button>
    </form>
  );
}

export function Prices() {
  const { selected } = useSelectedTenant();
  const [data, setData] = useState<PricesAnswer | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(async () => {
    if (!selected) return;
    const r = await api<PricesAnswer>(`/v1/zd/prices?tenant=${encodeURIComponent(selected.id)}`);
    if (r.ok) {
      setData(r.data);
      setLoadError(undefined);
    } else {
      setLoadError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("console.prices.load_failed")));
    }
  }, [selected]);

  useEffect(() => {
    setData(undefined);
    void load();
  }, [load]);

  return (
    <section aria-labelledby="prices-title">
      <h2 id="prices-title">{t("console.prices.title")}</h2>
      <p>{t("console.prices.intro")}</p>
      {!selected && <p>{t("console.prices.choose_tenant")}</p>}
      {selected && loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {selected && !data && !loadError && <p aria-busy="true">{t("console.prices.loading")}</p>}
      {selected && data && data.tenant.id === selected.id && (
        <>
          {data.onboardingPriceGroup && (
            <p>
              {t("console.prices.onboarding_group")} <strong>{data.onboardingPriceGroup}</strong>
            </p>
          )}
          <PriceTable m="lite" data={data} />
          <PriceTable m="remembrance" data={data} />
          <AddPrice key={data.tenant.id} data={data} onAdded={() => void load()} onReload={() => void load()} />
        </>
      )}
    </section>
  );
}
